import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Button, TextField, IconButton, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, Divider, Alert, FormControlLabel, Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { FinalSettlementService } from '../../services/finalSettlement.service';
import { EmployeeLetterService } from '../../services/employee-letter.service';
import { useToast } from '../../context/ToastContext';
import {
    IAdjustmentLine, IComputationNote, IEmployeeExit, IFinalSettlement,
} from '../../interfaces/payroll.interface';
import { formatMoney, humanizeEnum } from '../../utils/format';

const MAX_ADJUSTMENTS = 20;

// Every zero-reason / info code FinalSettlementService can write into computationNotes (spec
// §2.5 H6/H7), rendered human-readable. UNCONSUMED_ENCASHABLE_TYPE and PENDING_ENCASHMENT_UNPAYABLE
// are deliberately absent from this table -- both carry their own leaveTypeName/remaining payload
// and are special-cased in renderNote below instead of going through this static lookup (L-4,
// task-1 fix round 1: PENDING_ENCASHMENT_UNPAYABLE's omission from this comment was previously
// undocumented even though the code itself already special-cased it -- see renderNote).
//
// M4 fix: EL_TYPE_TENURE_GATED was emitted by the backend with no entry here (pre-existing
// defect) -- added below alongside the termination benefits REV 2 codes (D1/D7/R2/R5/R6/R11).
export const REASON_TEXT: Record<string, string> = {
    NO_ACTIVE_SALARY: 'No active salary found for the employee.',
    NO_BASIC_COMPONENT: 'No basic salary component designated (isOvertimeBase) — day-rate cannot be computed.',
    NO_JOINING_DATE: 'Joining date not set — service-based benefits computed as zero years.',
    GRATUITY_DISABLED: 'Gratuity is disabled in payroll settings.',
    GRATUITY_45_DAY_TIER: 'Service exceeds 10 years — statutory 45 days/year rate applied (overrides a lower configured rate).',
    NO_ENCASHABLE_TYPE: 'No active, paid, encashable leave type configured.',
    NO_ENCASHABLE_BALANCE: 'No positive encashable leave balance found.',
    EL_TYPE_TENURE_GATED: 'An encashable leave type with a positive balance was excluded — its accrual is not yet spendable (tenure gate).',
    COMPENSATION_TENURE_BELOW_5Y: 'Statutory compensation: below 5 completed years of service — nil.',
    COMPENSATION_TENURE_BELOW_1Y: 'Statutory compensation: below 1 completed year of service — nil.',
    COMPENSATION_NOT_APPLICABLE_ABSCONDING: 'Statutory compensation does not apply to absconding (statute silent — pending legal review).',
    SERVICE_BENEFIT_GRATUITY_HIGHER: 'Service benefit: gratuity is higher than statutory compensation.',
    SERVICE_BENEFIT_COMPENSATION_HIGHER: 'Service benefit: statutory compensation is higher than gratuity.',
    SERVICE_BENEFIT_EQUAL: 'Service benefit: gratuity and statutory compensation are equal.',
    SERVICE_BENEFIT_COMPENSATION_ONLY: 'Gratuity is disabled — the service benefit is the statutory compensation alone.',
    SERVICE_BENEFIT_NONE: 'Both the gratuity and statutory compensation are zero.',
    SERVICE_BENEFIT_FORFEITED_DISMISSAL: 'Service benefit forfeited on dismissal (admin action) — both amounts set to zero.',
    NOTICE_NOT_APPLICABLE_DISMISSAL: 'Notice pay is not applicable on dismissal (§23(1)(b): dismissal is without notice) — requested notice days were ignored.',
    FORFEIT_IGNORED_NON_DISMISSAL: 'Forfeit-service-benefit was requested but ignored — it only applies to a DISMISSAL exit.',
    TOTAL_NEGATIVE_RECOVERY_EXCEEDS_DUES: 'Total is negative — adjustments/recovery exceed dues. Finalize is blocked until this is resolved.',
};

const renderNote = (note: IComputationNote): string => {
    if (note.code === 'UNCONSUMED_ENCASHABLE_TYPE') {
        return `${note.leaveTypeName}: ${note.remaining} day(s) remaining, not drawn`;
    }
    // Final-review Finding 3: a live, not-yet-applied encashment whose target month a payroll run
    // can never reach for this employee once the exit excludes them -- surfaced so the admin
    // notices and deletes/handles it before finalizing.
    if (note.code === 'PENDING_ENCASHMENT_UNPAYABLE') {
        return `${note.leaveTypeName}: ${note.remaining} day(s) pending encashment can never be paid — delete or handle it before finalizing.`;
    }
    return REASON_TEXT[note.code] || note.code;
};

interface Props {
    exit: IEmployeeExit;
    settlement: IFinalSettlement | null;
    institutionId: string;
    onChange: () => void;
}

// Per-exit settlement panel (spec §2.5/§3): compute (notice days + adjustments editor) -> computed
// parts view with per-line "basic/30" day-rate basis labels and computation_notes reasons rendered
// human-readable -> status-driven buttons. DRAFT: Recompute/Finalize/Delete. FINALIZED:
// Un-finalize/Mark paid. PAID: Void. VOIDED: terminal (no actions). Every refusal (finalize
// over-drawn, negative total...) surfaces verbatim via showApiError, mirroring the encashment
// list's refusal handling.
const FinalSettlementPanel: React.FC<Props> = ({ exit, settlement, institutionId, onChange }) => {
    const { showSuccess, showApiError } = useToast();
    const [noticeDays, setNoticeDays] = useState(0);
    const [adjustments, setAdjustments] = useState<IAdjustmentLine[]>([]);
    const [forfeitServiceBenefit, setForfeitServiceBenefit] = useState(false);
    const [computing, setComputing] = useState(false);
    const [transitioning, setTransitioning] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    useEffect(() => {
        setNoticeDays(settlement?.noticeDays ?? 0);
        setAdjustments(settlement?.adjustments ?? []);
        setForfeitServiceBenefit(false);
    }, [exit.id, settlement]);

    const isDismissal = exit.exitType === 'DISMISSAL';

    // R6/H5: which candidate line(s) to render for the "Service Benefit" row -- driven off the
    // notes the backend already computed, never re-derived client-side.
    const isServiceBenefitCompensationOnly = (settlement?.computationNotes ?? [])
        .some((n) => n.code === 'SERVICE_BENEFIT_COMPENSATION_ONLY');
    const isServiceBenefitTieAtZero = (settlement?.computationNotes ?? [])
        .some((n) => n.code === 'SERVICE_BENEFIT_NONE');
    const isNegativeTotal = (settlement?.computationNotes ?? [])
        .some((n) => n.code === 'TOTAL_NEGATIVE_RECOVERY_EXCEEDS_DUES');

    const isDraft = !settlement || settlement.status === 'DRAFT';
    const isFinalized = settlement?.status === 'FINALIZED';
    const isPaid = settlement?.status === 'PAID';
    const isVoided = settlement?.status === 'VOIDED';

    const addAdjustment = () => {
        if (adjustments.length >= MAX_ADJUSTMENTS) return;
        setAdjustments([...adjustments, { label: '', amount: 0 }]);
    };

    const removeAdjustment = (idx: number) => {
        setAdjustments(adjustments.filter((_, i) => i !== idx));
    };

    // Fix round 1 (F7): an intermediate typed value like "-" or "1e" parses to NaN, which
    // JSON.stringify writes as `null` — that trips AdjustmentLine's @NotNull with an opaque
    // bean-validation body instead of this page's own guard. Keep the previous amount rather
    // than committing a non-finite one; the field still shows whatever the user is mid-typing
    // via its own `value` prop, so this only guards what gets sent, not what's displayed.
    const updateAdjustment = (idx: number, field: keyof IAdjustmentLine, value: string) => {
        setAdjustments(adjustments.map((a, i) => {
            if (i !== idx) return a;
            if (field !== 'amount') return { ...a, [field]: value };
            const next = Number(value);
            return Number.isFinite(next) ? { ...a, amount: next } : a;
        }));
    };

    const hasNonFiniteAdjustment = adjustments.some((a) => !Number.isFinite(a.amount));

    const handleCompute = async () => {
        setComputing(true);
        try {
            await FinalSettlementService.compute(institutionId, exit.id, {
                noticeDays,
                adjustments: adjustments.filter((a) => a.label.trim().length > 0),
                // R2: meaningful only for DISMISSAL -- the backend ignores it (with a note) for
                // any other exit type, but there is no reason to send it non-false otherwise.
                forfeitServiceBenefit: isDismissal ? forfeitServiceBenefit : false,
            });
            showSuccess(settlement ? 'Settlement recomputed' : 'Settlement computed');
            onChange();
        } catch (error) {
            showApiError(error, 'Failed to compute settlement');
        } finally {
            setComputing(false);
        }
    };

    const transition = async (
        action: (id: string, institutionId: string) => Promise<IFinalSettlement>,
        successMessage: string,
        failureMessage: string
    ) => {
        if (!settlement) return;
        setTransitioning(true);
        try {
            await action(settlement.id, institutionId);
            showSuccess(successMessage);
            onChange();
        } catch (error) {
            showApiError(error, failureMessage);
        } finally {
            setTransitioning(false);
        }
    };

    const handleFinalize = () => transition(FinalSettlementService.finalize, 'Settlement finalized', 'Failed to finalize settlement');
    const handleUnfinalize = () => transition(FinalSettlementService.unfinalize, 'Settlement un-finalized', 'Failed to un-finalize settlement');
    const handlePay = () => transition(FinalSettlementService.pay, 'Settlement marked paid', 'Failed to mark settlement paid');
    const handleVoid = () => transition(FinalSettlementService.voidSettlement, 'Settlement voided', 'Failed to void settlement');

    const handleDownloadPdf = async () => {
        if (!settlement) return;
        setDownloadingPdf(true);
        try {
            await EmployeeLetterService.downloadFinalSettlement(settlement.id, settlement.username);
        } catch (error) {
            showApiError(error, 'Failed to download settlement statement');
        } finally {
            setDownloadingPdf(false);
        }
    };

    const handleDelete = async () => {
        if (!settlement) return;
        setTransitioning(true);
        try {
            await FinalSettlementService.delete(settlement.id, institutionId);
            showSuccess('Settlement deleted');
            onChange();
        } catch (error) {
            showApiError(error, 'Failed to delete settlement');
        } finally {
            setTransitioning(false);
        }
    };

    const statusColor: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
        DRAFT: 'default', FINALIZED: 'warning', PAID: 'success', VOIDED: 'error',
    };

    return (
        <Paper sx={{ p: 2, mt: 2 }} variant="outlined" data-help-section="payroll.settlement.panel">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6">Final Settlement</Typography>
                {settlement && (
                    <Chip data-help="payroll.settlement.status" size="small" label={humanizeEnum(settlement.status)} color={statusColor[settlement.status]} />
                )}
            </Box>

            {/* Fix round 1 (F6): dropped the redundant `&& !isVoided` — statuses are mutually
                exclusive, so isVoided can never be true while isDraft is. */}
            {isDraft && (
                <Box sx={{ mb: 2 }}>
                    <TextField
                        data-help="payroll.settlement.noticeDays"
                        label="Notice Days"
                        type="number"
                        size="small"
                        value={noticeDays}
                        onChange={(e) => setNoticeDays(Number(e.target.value))}
                        inputProps={{ min: 0 }}
                        disabled={isDismissal}
                        helperText={isDismissal ? 'Notice pay does not apply on dismissal (§23(1)(b)).' : undefined}
                        sx={{ mb: 2, maxWidth: 200 }}
                    />
                    {/* L-3 (task-1 fix round 1): removed a dead "notice days will be ignored"
                        warning Alert here -- it was gated on isDismissal && noticeDays > 0, but the
                        Notice Days input above is `disabled` for DISMISSAL and its value is reset
                        from settlement?.noticeDays, which the backend forces to 0 for every
                        DISMISSAL settlement (R5). The condition could never be true. */}
                    {isDismissal && (
                        <FormControlLabel
                            data-help="payroll.settlement.forfeit"
                            sx={{ mb: 2, display: 'block' }}
                            control={(
                                <Checkbox
                                    checked={forfeitServiceBenefit}
                                    onChange={(e) => setForfeitServiceBenefit(e.target.checked)}
                                />
                            )}
                            label="Forfeit the whole service benefit (§23 proviso — conviction / specified grave misconduct)"
                        />
                    )}

                    <Typography data-help="payroll.settlement.adjustments" variant="subtitle2" sx={{ mb: 1 }}>Adjustments</Typography>
                    {adjustments.map((adj, idx) => (
                        <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                            <TextField
                                data-help="payroll.settlement.adjLabel"
                                label="Label"
                                size="small"
                                value={adj.label}
                                onChange={(e) => updateAdjustment(idx, 'label', e.target.value)}
                                inputProps={{ maxLength: 100 }}
                            />
                            <TextField
                                data-help="payroll.settlement.adjAmount"
                                label="Amount"
                                type="number"
                                size="small"
                                value={adj.amount}
                                onChange={(e) => updateAdjustment(idx, 'amount', e.target.value)}
                            />
                            <IconButton
                                data-help="payroll.settlement.adjRemove"
                                size="small"
                                aria-label={`Remove adjustment ${idx + 1}`}
                                onClick={() => removeAdjustment(idx)}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                    <Button
                        data-help="payroll.settlement.adjAdd"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addAdjustment}
                        disabled={adjustments.length >= MAX_ADJUSTMENTS}
                    >
                        Add Adjustment
                    </Button>

                    <Box sx={{ mt: 2 }}>
                        <Button data-help="payroll.settlement.compute" variant="contained" onClick={handleCompute} disabled={computing || hasNonFiniteAdjustment}>
                            {settlement ? 'Recompute' : 'Compute'}
                        </Button>
                    </Box>
                </Box>
            )}

            {settlement && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <TableContainer data-help-section="payroll.settlement.table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="payroll.settlement.col.line">Line</TableCell>
                                    <TableCell data-help="payroll.settlement.col.basis">Basis</TableCell>
                                    <TableCell align="right" data-help="payroll.settlement.col.amount">Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>Earned Leave Encashment ({settlement.elDays} day(s){settlement.leaveTypeNameNormalized ? ` — ${settlement.leaveTypeNameNormalized}` : ''})</TableCell>
                                    <TableCell>basic/30</TableCell>
                                    <TableCell align="right">{settlement.elAmount != null ? formatMoney(settlement.elAmount) : '—'}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Notice Pay ({settlement.noticeDays} day(s))</TableCell>
                                    <TableCell>basic/30</TableCell>
                                    <TableCell align="right">{settlement.noticeAmount != null ? formatMoney(settlement.noticeAmount) : '—'}</TableCell>
                                </TableRow>
                                {/* D7/R6/M5: serviceBenefitAmount is null ONLY on a pre-V123 legacy row --
                                    render the historical single "Gratuity" row for those, byte-identical
                                    to the old behavior. Every row computed from V123 onward renders the
                                    new "Service Benefit" row instead, with the winning candidate(s) shown
                                    as indented sub-lines so the whichever-higher outcome stays visible. */}
                                {settlement.serviceBenefitAmount == null ? (
                                    <TableRow>
                                        <TableCell>Gratuity{settlement.serviceYears != null ? ` (${settlement.serviceYears} yr service)` : ''}</TableCell>
                                        <TableCell>basic/30</TableCell>
                                        <TableCell align="right">{settlement.gratuityAmount != null ? formatMoney(settlement.gratuityAmount) : '—'}</TableCell>
                                    </TableRow>
                                ) : (
                                    <>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Service Benefit</TableCell>
                                            <TableCell>whichever higher</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatMoney(settlement.serviceBenefitAmount)}</TableCell>
                                        </TableRow>
                                        {/* H5/R6: a gratuity-disabled institution never had a gratuity
                                            candidate to compare against -- no candidate line renders for
                                            it, and a genuine zero-zero tie renders NO candidate lines at
                                            all (there is no contest to show). */}
                                        {!isServiceBenefitCompensationOnly && !isServiceBenefitTieAtZero && (
                                            <TableRow>
                                                <TableCell sx={{ pl: 4 }} colSpan={2}>
                                                    Gratuity{settlement.serviceYears != null ? ` — ${settlement.serviceYears} yr service` : ''}
                                                </TableCell>
                                                <TableCell align="right">{settlement.gratuityAmount != null ? formatMoney(settlement.gratuityAmount) : '—'}</TableCell>
                                            </TableRow>
                                        )}
                                        {!isServiceBenefitTieAtZero && (
                                            <TableRow>
                                                <TableCell sx={{ pl: 4 }} colSpan={2}>
                                                    Statutory compensation{settlement.compensationDaysPerYear != null ? ` @ ${settlement.compensationDaysPerYear}d/yr` : ''}
                                                    {settlement.compensationYears != null ? ` — ${settlement.compensationYears} yr service` : ''}
                                                </TableCell>
                                                <TableCell align="right">{settlement.statutoryCompensationAmount != null ? formatMoney(settlement.statutoryCompensationAmount) : '—'}</TableCell>
                                            </TableRow>
                                        )}
                                    </>
                                )}
                                {settlement.adjustments.map((adj, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>{adj.label}</TableCell>
                                        <TableCell>adjustment</TableCell>
                                        <TableCell align="right">{formatMoney(adj.amount)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                    <TableCell />
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {settlement.totalAmount != null ? formatMoney(settlement.totalAmount) : '—'}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {settlement.finalPayrollId == null && (
                        <Alert severity="info" sx={{ mt: 2 }}>Payroll row unavailable for the exit month.</Alert>
                    )}

                    {/* R11: surfaced at compute-time, before Finalize is even attempted, so the
                        admin sees why the button will refuse rather than clicking into a 400. */}
                    {isNegativeTotal && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            Total is negative — Finalize will be refused until adjustments/recovery
                            no longer exceed dues.
                        </Alert>
                    )}

                    {settlement.computationNotes.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography data-help="payroll.settlement.notes" variant="subtitle2">Computation Notes</Typography>
                            {settlement.computationNotes.map((note, idx) => (
                                <Typography key={idx} variant="body2" color="text.secondary">
                                    {renderNote(note)}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                        <Button data-help="payroll.settlement.pdf" variant="outlined" onClick={handleDownloadPdf} disabled={downloadingPdf}>
                            Download PDF
                        </Button>
                        {isDraft && (
                            <>
                                <Button
                                    data-help="payroll.settlement.finalize"
                                    variant="contained"
                                    color="primary"
                                    onClick={handleFinalize}
                                    disabled={transitioning || isNegativeTotal}
                                    title={isNegativeTotal ? 'Total is negative — resolve adjustments/recovery before finalizing.' : undefined}
                                >
                                    Finalize
                                </Button>
                                <Button data-help="payroll.settlement.delete" variant="outlined" color="error" onClick={handleDelete} disabled={transitioning}>
                                    Delete
                                </Button>
                            </>
                        )}
                        {isFinalized && (
                            <>
                                <Button data-help="payroll.settlement.unfinalize" variant="outlined" onClick={handleUnfinalize} disabled={transitioning}>
                                    Un-finalize
                                </Button>
                                <Button data-help="payroll.settlement.pay" variant="contained" color="primary" onClick={handlePay} disabled={transitioning}>
                                    Mark Paid
                                </Button>
                            </>
                        )}
                        {isPaid && (
                            <Button data-help="payroll.settlement.void" variant="outlined" color="error" onClick={handleVoid} disabled={transitioning}>
                                Void
                            </Button>
                        )}
                        {isVoided && (
                            <Typography variant="body2" color="text.secondary">
                                This settlement has been voided — no further actions are available.
                            </Typography>
                        )}
                    </Box>
                </>
            )}
        </Paper>
    );
};

export default FinalSettlementPanel;
